import appVariables from '@/config'
import { Allergen, CerealSubAllergen, Dietary, NutSubAllergen } from '@/generated/gql/graphql'
import { numberFormat } from '@/helpers/numbers'
import { DietaryRow } from '@fusion/dietary/row'
import { getCloudflareImageUrl } from '@fusion/media/cloudflare'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import { Avatar, Box, Card, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import NoImageAvailable from '@public/images/no-image-available.png'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

import { optionsAreValid } from '../ccMenuSpecificHelpers'

export type OrderAgainItemCardData = {
  itemReference: string
  orderCount: number
  averageRating: number | null
  name: string | null
  image: string | null
  priceCustomerFacing: number | null
  dietaries: Dietary[]
  allergens: Allergen[]
  subAllergens: {
    nuts: NutSubAllergen[]
    cereals: CerealSubAllergen[]
  }
  options:
    | ({
        _id: string
        minQty: number | null
        maxQty: number | null
        qtyPerPerson: number | null
      } | null)[]
    | null
}

type Props = {
  itemData: OrderAgainItemCardData
  vendorName?: string | null
  vendorLogo?: string | null
  hidePrices: boolean
  onAddToCart: (itemReference: string) => void
  onShowOptions: (itemReference: string) => void
  disableAddToCart?: boolean
}

export const ORDER_AGAIN_CARD_WIDTH = 190
const ACTION_BUTTON_SIZE = 32
const CARD_IMAGE_HEIGHT = Math.round((ORDER_AGAIN_CARD_WIDTH * 2) / 3)

const OrderAgainItemCard: React.FC<Props> = ({
  itemData,
  vendorName,
  vendorLogo,
  hidePrices,
  onAddToCart,
  onShowOptions,
  disableAddToCart = false,
}) => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({ triggerOnce: true })
  const hasOptions = Boolean(itemData.options && itemData.options.length > 0)

  const nameRef = useRef<HTMLSpanElement>(null)
  const [nameTruncated, setNameTruncated] = useState(false)

  useEffect(() => {
    const el = nameRef.current
    if (el) setNameTruncated(el.scrollWidth > el.clientWidth)
  }, [itemData.name])

  const handleShowOptions = useCallback(() => {
    onShowOptions(itemData.itemReference)
  }, [onShowOptions, itemData.itemReference])

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (itemData.options && optionsAreValid(itemData.options, {})) {
        onAddToCart(itemData.itemReference)
      } else {
        onShowOptions(itemData.itemReference)
      }
    },
    [onAddToCart, onShowOptions, itemData],
  )

  const handleShowOptionsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      handleShowOptions()
    },
    [handleShowOptions],
  )

  const imageUrl = itemData.image
    ? getCloudflareImageUrl({
        enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
        src: itemData.image,
        width: 500,
        quality: 100,
      })
    : NoImageAvailable.src

  const vendorLogoUrl = vendorLogo
    ? getCloudflareImageUrl({
        enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
        src: vendorLogo,
        width: 100,
        quality: 100,
      })
    : undefined

  const formattedPrice = useMemo(
    () => numberFormat((itemData.priceCustomerFacing || 0) / 100),
    [itemData.priceCustomerFacing],
  )

  const pill = useMemo(() => {
    if (itemData.averageRating !== null) {
      return {
        label: t('cc:orderAgain.rated', { rating: itemData.averageRating.toFixed(1) }),
        tooltip: t('cc:orderAgain.orderCountTooltip', { count: itemData.orderCount }),
        rated: true,
      }
    }
    if (itemData.orderCount === 1) {
      return { label: t('cc:orderAgain.orderedOnce'), tooltip: '', rated: false }
    }
    if (itemData.orderCount === 2) {
      return { label: t('cc:orderAgain.orderedTwice'), tooltip: '', rated: false }
    }
    return {
      label: t('cc:orderAgain.orderedMultiple', { count: itemData.orderCount }),
      tooltip: '',
      rated: false,
    }
  }, [itemData.averageRating, itemData.orderCount, t])

  return (
    <Card
      onClick={handleShowOptions}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
      }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: CARD_IMAGE_HEIGHT,
          flexShrink: 0,
          bgcolor: 'grey.200',
        }}>
        <Box
          ref={ref}
          component="img"
          src={inView ? imageUrl : undefined}
          alt={itemData.name ?? ''}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {vendorName && vendorLogoUrl ? (
          <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
            <Tooltip title={vendorName} placement="top" arrow>
              <Avatar
                alt={vendorName}
                src={vendorLogoUrl}
                sx={{
                  width: 24,
                  height: 24,
                  border: '2px solid',
                  borderColor: 'common.white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
              />
            </Tooltip>
          </Box>
        ) : null}
        <Tooltip title={pill.tooltip} placement="top" arrow disableHoverListener={!pill.tooltip}>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              maxWidth: 'calc(100% - 16px)',
              padding: '4px 8px',
              borderRadius: 10,
              ...(pill.rated
                ? { bgcolor: (theme) => theme.palette.secondary.main }
                : { bgcolor: 'rgba(15,15,30,0.52)', backdropFilter: 'blur(6px)' }),
            }}>
            <Typography
              variant="caption"
              fontWeight={600}
              noWrap
              sx={{
                display: 'block',
                fontSize: '10px',
                color: pill.rated ? 'secondary.contrastText' : 'common.white',
              }}>
              {pill.label}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <Stack sx={{ p: 1.25, gap: 0.75 }}>
        <Tooltip title={nameTruncated ? itemData.name : ''} placement="top" arrow>
          <Typography ref={nameRef} variant="body2" fontWeight={600} noWrap>
            {itemData.name}
          </Typography>
        </Tooltip>
        <DietaryRow
          isComboItem={false}
          maxToShow={3}
          subAllergens={itemData.subAllergens}
          allergens={itemData.allergens}
          dietaries={itemData.dietaries}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          {!hidePrices ? (
            <Typography variant="body2" fontWeight={600} noWrap>
              {`${formattedPrice} Credits`}
            </Typography>
          ) : null}
          <Stack direction="row" gap={0.75} flexShrink={0}>
            {hasOptions ? (
              <IconButton
                onClick={handleShowOptionsClick}
                aria-label={t('cc:orderAgain.showOptions')}
                sx={{
                  width: ACTION_BUTTON_SIZE,
                  height: ACTION_BUTTON_SIZE,
                  border: '1.5px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }}>
                <TuneIcon sx={{ width: 16, height: 16 }} />
              </IconButton>
            ) : null}
            <IconButton
              onClick={handleAddToCart}
              disabled={disableAddToCart}
              aria-label={t('cc:addToCart', { count: 1 })}
              sx={{
                width: ACTION_BUTTON_SIZE,
                height: ACTION_BUTTON_SIZE,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}>
              <ShoppingCartOutlinedIcon sx={{ width: 16, height: 16 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
}

export default React.memo(OrderAgainItemCard)
